import type { FC, ReactNode } from 'react'

import { useId, useState } from 'react'
import { ChevronDown, MessageCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import Container from '@/components/landing/container'
import SectionHeading from '@/components/landing/section-heading'

import { FAQS, LINKS } from '@/constants/landing'

const FaqAccordion: FC = (): ReactNode => {
    const [openIndex, setOpenIndex] = useState<number | null>(0)
    const baseId = useId()

    return (
        <div className='divide-y divide-border rounded-[1.75rem] border border-border bg-background'>
            {FAQS.map((item, index) => {
                const isOpen = openIndex === index
                const buttonId = `${baseId}-question-${index}`
                const panelId = `${baseId}-answer-${index}`

                return (
                    <div key={item.question} className='px-5 sm:px-8'>
                        <h3>
                            <button
                                id={buttonId}
                                type='button'
                                aria-expanded={isOpen}
                                aria-controls={panelId}
                                onClick={() => setOpenIndex(isOpen ? null : index)}
                                className='flex w-full items-center justify-between gap-6 py-6 text-left text-base font-semibold text-foreground transition-colors hover:text-primary sm:text-lg'
                            >
                                {item.question}

                                <span
                                    aria-hidden='true'
                                    className={cn(
                                        'grid size-9 shrink-0 place-items-center rounded-full border transition-[transform,background-color,border-color,color] duration-300',
                                        isOpen ? 'rotate-180 border-primary bg-primary text-primary-foreground' : 'border-border text-foreground'
                                    )}
                                >
                                    <ChevronDown className='size-4' />
                                </span>
                            </button>
                        </h3>

                        <div
                            id={panelId}
                            role='region'
                            aria-labelledby={buttonId}
                            className={cn(
                                'grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none',
                                isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                            )}
                        >
                            <div className='overflow-hidden' aria-hidden={!isOpen}>
                                <p className='pb-6 pr-4 leading-relaxed text-muted-foreground sm:pr-12'>
                                    {item.answer}
                                </p>
                            </div>
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

const FaqSection: FC = (): ReactNode => {
    return (
        <section id='faq' aria-labelledby='faq-title' className='bg-card py-20 sm:py-28'>
            <Container className='grid gap-12 lg:grid-cols-12 lg:gap-16'>
                <div className='lg:col-span-5'>
                    <SectionHeading
                        id='faq-title'
                        eyebrow="FAQ"
                        title="Your questions, answered"
                        description="The things learners and recruiters ask us most before getting started."
                    />

                    <div className='mt-10 rounded-3xl bg-background p-6 ring-1 ring-border sm:p-7'>
                        <p className='font-heading text-lg font-semibold text-foreground'>
                            Still have a question?
                        </p>

                        <p className='mt-1 text-[15px] text-muted-foreground'>
                            Message our team on WhatsApp or ask for a callback.
                        </p>

                        <div className='mt-5 flex flex-wrap gap-3'>
                            <a
                                href={LINKS.whatsappLearner}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='inline-flex h-11 items-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-brand-navy'
                            >
                                <MessageCircle className='size-4' aria-hidden='true' />
                                Chat on WhatsApp
                                <span className='sr-only'>(opens in a new tab)</span>
                            </a>

                            <a
                                href='#contact'
                                className='inline-flex h-11 items-center rounded-full border border-border bg-card px-5 text-sm font-semibold text-foreground transition-colors hover:border-primary/40 hover:text-primary'
                            >
                                Request a callback
                            </a>
                        </div>
                    </div>
                </div>

                <div className='lg:col-span-7'>
                    <FaqAccordion />
                </div>
            </Container>
        </section>
    )
}

export default FaqSection
